import { LuUsers, LuUserRoundCog, LuFolderOpen } from "react-icons/lu";
import {
  TbCalendarTime,
  TbPill,
  TbFirstAidKit,
  TbClipboardHeart,
  TbLayoutDashboard,
} from "react-icons/tb";

// Only features that exist in the product today. Keep this list honest —
// add a card here when a feature ships, not before.
export const FEATURES = [
  {
    icon: LuUsers,
    tone: "magenta",
    title: "Service users",
    body: "Profiles with medical details, GP practice, next of kin, address and contact details — all in one record.",
  },
  {
    icon: LuUserRoundCog,
    tone: "violet",
    title: "Staff & access",
    body: "Team member records, employment details and role-based access for administrators and field carers.",
  },
  {
    icon: TbCalendarTime,
    tone: "navy",
    title: "Rotas & shifts",
    body: "Create, clone and edit shifts, match staff to service users and record retrospective shifts when needed.",
  },
  {
    icon: TbPill,
    tone: "magenta",
    title: "Medication (eMAR)",
    body: "Medication groups with dosage, form, route and instructions. Every administration is recorded against the shift.",
  },
  {
    icon: TbFirstAidKit,
    tone: "violet",
    title: "PRN medication",
    body: "Record as-needed medication with quantity and notes, and keep a full administration history per service user.",
  },
  {
    icon: TbClipboardHeart,
    tone: "navy",
    title: "Care plans & risk assessments",
    body: "Build reusable templates and complete care plans and risk assessments for each service user.",
  },
  {
    icon: LuFolderOpen,
    tone: "magenta",
    title: "Documents",
    body: "Upload and organise documents into groups against service users and staff members.",
  },
  {
    icon: TbLayoutDashboard,
    tone: "violet",
    title: "Dashboard",
    body: "See shift status across your service at a glance — scheduled, in progress and completed.",
  },
];

export const SHIFT_FLOW = [
  {
    step: "01",
    title: "Plan the rota",
    body: "Office staff schedule shifts on the web dashboard and assign a carer to each service user.",
  },
  {
    step: "02",
    title: "Clock in on arrival",
    body: "The carer opens the shift on their phone and clocks in — the office sees it move to In progress.",
  },
  {
    step: "03",
    title: "Record the care",
    body: "Add care notes, administer scheduled medication and record any PRN given during the visit.",
  },
  {
    step: "04",
    title: "Clock out & review",
    body: "Clocking out completes the shift. Managers review notes and administrations from the shift record.",
  },
];

// Domain glossary — the words used across the web dashboard and mobile app.
export const GLOSSARY = [
  {
    term: "Service user",
    def: "A person receiving care. Their record holds personal, medical, GP and next of kin details.",
  },
  {
    term: "Staff member",
    def: "A member of your care team. Staff records hold contact and employment details.",
  },
  {
    term: "Field user",
    def: "A carer who works in the community. Field users see their own shifts in the mobile app.",
  },
  {
    term: "Rota",
    def: "The schedule of shifts across your service, managed from the web dashboard.",
  },
  {
    term: "Shift",
    def: "A scheduled visit linking a staff member to a service user, with a start time, end time and type.",
  },
  {
    term: "Clock in / clock out",
    def: "The carer's recorded arrival and departure times for a shift.",
  },
  {
    term: "Care note",
    def: "A written record of the care given, added to a shift by the carer.",
  },
  {
    term: "eMAR",
    def: "Electronic Medication Administration Record — the digital record of every medication given.",
  },
  {
    term: "Medication group",
    def: "A set of medications given together, for example Morning meds, assigned to a service user.",
  },
  {
    term: "PRN",
    def: "Pro re nata — medication given only when needed, such as pain relief, rather than on a schedule.",
  },
  {
    term: "Administered medication",
    def: "A recorded dose: what was given, when, by whom and against which shift.",
  },
  {
    term: "Care plan",
    def: "A service user's documented care needs and how they should be met.",
  },
  {
    term: "Risk assessment",
    def: "A structured record of risks to a service user, such as risk of falls, and how they are managed.",
  },
  {
    term: "Template",
    def: "A reusable form, such as a care plan or risk assessment, completed for each service user.",
  },
  {
    term: "Document group",
    def: "A folder used to organise uploaded documents against a person's record.",
  },
  {
    term: "Next of kin (NOK)",
    def: "The service user's nominated contact, recorded with their relationship and contact details.",
  },
];
